import {formatByteSize} from '@/shared/lib/format-byte-size';
import {formatImageMimeTypes} from '@/shared/lib/format-image-mime-types';
import {MAX_DECODE_MEGAPIXELS, MAX_INPUT_BYTES} from './constants';
import type {ImagePreprocessErrorCode} from './types';

export type ImagePreprocessMessageContext = {
  readonly acceptedMimeTypes: readonly string[];
  readonly maxBytes: number;
};

const MESSAGE_BY_CODE: Readonly<
  Record<ImagePreprocessErrorCode, (context: ImagePreprocessMessageContext) => string>
> = {
  unreadable: () => 'Could not read this image. Try another one.',
  'unsupported-format': ({acceptedMimeTypes}) =>
    `Choose a ${formatImageMimeTypes([...acceptedMimeTypes, 'image/heic'])} image.`,
  'input-too-large': () => `Choose an image smaller than ${formatByteSize(MAX_INPUT_BYTES)}.`,
  'too-many-pixels': () => `Choose an image under ${MAX_DECODE_MEGAPIXELS} megapixels.`,
  'decode-failed': () => 'Could not read this image. Try another one.',
  'encode-failed': () => 'Could not prepare this image for upload. Try another one.',
  'output-too-large': ({maxBytes}) =>
    `This image is still larger than ${formatByteSize(maxBytes)} after resizing. Choose a smaller image.`,
  aborted: () => 'Image preparation was cancelled.',
};

/** User-facing failure of the preprocessing helper; callers show `message` verbatim unless `code` is `aborted`. */
export class ImagePreprocessError extends Error {
  override readonly name = 'ImagePreprocessError';

  constructor(
    readonly code: ImagePreprocessErrorCode,
    context: ImagePreprocessMessageContext,
    options?: {readonly cause?: unknown},
  ) {
    super(MESSAGE_BY_CODE[code](context), options);
  }
}
